/**
 * TourRunner — el tour de una lección sobre la pantalla real (driver.js).
 *
 *   correrTour(leccion, { onInicio, onPaso, onFin })   arma los pasos con los [data-tour="…"] que están a la vista y los corre.
 *                                                      Un paso cuyo elemento no existe (rol sin esa sección, pantalla vacía) se SALTA sin error.
 *   lanzarTour(leccion, { navigate, …hooks })          va a la `ruta` de la lección si hace falta, espera a que carguen los elementos y corre.
 *   <TourAutoStart />                                  (montado una vez en Layout) lo lanza solo la primera vez que alguien abre la ruta de una
 *                                                      lección nueva de su ruta. Nunca encima de la bienvenida, en modo prueba, ni con otra ventana abierta.
 *
 * Salir con Esc o «Saltar» cuenta como «visto»; solo llegar al último paso cuenta como «completado».
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour.css";
import { useAuth } from "../context/AuthContext.jsx";
import { useFlag } from "../lib/useFlags.js";
import { useUserPref } from "../hooks/useUserPref.js";
import { useAccesoEstado } from "../lib/useAccesoEstado.js";
import { useAcademia, registrarEvento } from "./useAcademia.js";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const pausa = (ms) => new Promise(r => setTimeout(r, ms));

function visible(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return false;
  const cs = getComputedStyle(el);
  return cs.visibility !== "hidden" && cs.display !== "none";
}

/** Pasos de la lección cuyo elemento está en pantalla ahora mismo. */
export function pasosPresentes(leccion) {
  return (leccion?.pasos || [])
    .filter(p => p.target)                      // un paso sin elemento (solo texto) queda para la guía
    .map(p => ({ p, el: document.querySelector(`[data-tour="${p.target}"]`) }))
    .filter(x => visible(x.el));
}

/** Espera (hasta `ms`) a que haya al menos `minimo` elementos del tour a la vista. Devuelve cuántos hay. */
export async function esperarPasos(leccion, { ms = 4000, minimo = 1 } = {}) {
  const t0 = Date.now();
  for (;;) {
    const n = pasosPresentes(leccion).length;
    if (n >= minimo || Date.now() - t0 > ms) return n;
    await pausa(250);
  }
}

export function correrTour(leccion, { onInicio, onPaso, onFin } = {}) {
  const pasos = pasosPresentes(leccion);
  if (!pasos.length) return null;
  let ultimo = -1;
  let terminado = false;

  const fin = () => {
    if (terminado) return;
    terminado = true;
    onFin?.({ completado: ultimo >= pasos.length - 1, ultimo: ultimo + 1, total: pasos.length });
  };

  const d = driver({
    showProgress: true,
    progressText: "{{current}} de {{total}}",
    nextBtnText: "Siguiente",
    prevBtnText: "Atrás",
    doneBtnText: "Listo",
    popoverClass: "crew-tour",
    allowClose: true,
    overlayOpacity: 0.55,
    stagePadding: 8,
    stageRadius: 16,
    steps: pasos.map(({ p, el }, i) => ({
      element: el,
      popover: {
        title: `<span class="crew-tour-n">${i + 1}</span>${esc(p.titulo)}`,
        description: `<p class="crew-tour-t">${esc(p.texto)}</p>${p.tip ? `<p class="crew-tour-tip">${esc(p.tip)}</p>` : ""}`,
      },
    })),
    onPopoverRender: (popover) => { if (popover?.closeButton) popover.closeButton.textContent = "Saltar"; },
    onHighlighted: (_el, _step, { state }) => {
      const i = state?.activeIndex ?? 0;
      if (i > ultimo) { ultimo = i; onPaso?.(i + 1, pasos.length); }
    },
    onDestroyed: fin,
  });

  onInicio?.({ total: pasos.length });
  d.drive();
  return d;
}

/** Va a la pantalla de la lección (si hace falta), espera a que carguen sus elementos y corre el tour. "sin-pasos" si no hay nada que mostrar. */
export async function lanzarTour(leccion, { navigate, ...hooks } = {}) {
  // la ruta puede traer query (p. ej. /inicio?vista=personal): se compara completa para que dirección llegue a su Mi Día personal
  const ruta = (leccion.ruta || "").replace(/\/$/, "");
  const aca = window.location.pathname.replace(/\/$/, "") + window.location.search;
  if (ruta && navigate && aca !== ruta) {
    navigate(ruta);
    await pausa(350);
  }
  await esperarPasos(leccion);
  await pausa(700);                    // deja terminar los bloques que cargan datos (progreso del día, listas)
  return correrTour(leccion, hooks) ? "ok" : "sin-pasos";
}

/** Hooks estándar del tour: marca visto/completado y manda los eventos. Los usa el Centro de ayuda y el arranque automático. */
export function hooksDeAvance(leccion, { rol, marcarVisto, registrarTour }) {
  const totales = (leccion.pasos || []).length;
  return {
    onInicio: ({ total } = {}) => { marcarVisto(leccion); registrarEvento("academia_tour_inicio", leccion, rol, `visibles=${total ?? 0}&totales=${totales}`); },
    onPaso: (n, total) => registrarEvento("academia_tour_paso", leccion, rol, `paso=${n}&de=${total ?? 0}`),
    // Se guarda siempre cuántos pasos vio (aunque salga antes); solo llegar al último paso cuenta como completada.
    onFin: ({ completado, ultimo, total }) => {
      registrarTour(leccion, { vistos: ultimo, visibles: total, completado });
      registrarEvento("academia_tour_fin", leccion, rol, `completado=${completado ? 1 : 0}&ultimo=${ultimo}&total=${total}&totales=${totales}`);
    },
  };
}

/** Arranque automático: la primera vez que alguien abre la ruta de una lección nueva de su ruta. */
export function TourAutoStart() {
  const { miembro } = useAuth();
  const flagOn = useFlag("ff_academia");
  const location = useLocation();
  const navigate = useNavigate();
  const acceso = useAccesoEstado();
  const [bienvenida, , { synced: bienvenidaLista } = {}] = useUserPref("bienvenida_soa10pro", null);
  const ac = useAcademia();
  const intentadas = useRef(new Set());
  const enCurso = useRef(false);

  useEffect(() => {
    if (!flagOn || !miembro || !ac.synced || !bienvenidaLista || !acceso) return;
    if (acceso.restringido) return;                          // modo prueba del candado: no se muestra ni se guarda nada
    if (!(bienvenida && bienvenida.at)) return;              // la bienvenida va primero (si todavía no la vieron, no se mete encima)
    if (enCurso.current) return;
    if (/[?&]focus=/.test(location.search)) return;          // links directos de trabajo urgente
    const ruta = location.pathname.replace(/\/$/, "") || "/";
    const leccion = ac.lecciones.find(l => l.ruta && l.ruta.split("?")[0].replace(/\/$/, "") === ruta && ac.estadoDe(l) === "nueva" && !intentadas.current.has(l.id));
    if (!leccion) return;

    let cancelado = false;
    enCurso.current = true;
    (async () => {
      await pausa(1200);                                     // que la pantalla termine de acomodarse
      const hay = await esperarPasos(leccion, { ms: 5000, minimo: 2 });
      await pausa(600);
      const ventanaAbierta = document.querySelector('[role="dialog"], [role="alertdialog"]');
      if (cancelado || hay < 2 || ventanaAbierta) { enCurso.current = false; return; }
      intentadas.current.add(leccion.id);                    // recién acá cuenta como intento: si se canceló antes, se reintenta
      const hooks = hooksDeAvance(leccion, ac);
      correrTour(leccion, { ...hooks, onFin: (r) => { enCurso.current = false; hooks.onFin(r); } });
    })();
    return () => { cancelado = true; enCurso.current = false; };
  }, [flagOn, miembro?.id, ac.synced, bienvenidaLista, bienvenida, acceso, location.pathname, location.search, ac.lecciones, navigate]);

  return null;
}
